import { useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { SectionCard } from "shared/app/SectionCard";
import {
  Select,
  Button,
  ButtonVariant,
  ButtonSize,
  Show
} from "shared/components";
import type { BaseOption } from "shared/components";
import { useInboxToken } from "../../inbox/api/useInboxToken";
import {
  useAllPhoneNumbers,
  usePropertyPhoneDefault,
  useSetPropertyPhoneDefault
} from "../../phone-numbers/lib/hooks";
import type { PhoneNumber } from "../../phone-numbers/lib/types";
import {
  usePropertyDefaultEmail,
  useSetPropertyDefaultEmail
} from "../lib/propertyDefaultEmail";
import { formatPhone } from "shared/utils/format-phone";

const CLEAR_DEFAULT_VALUE = "";

// Constant on purpose: `resolved_email` was a moving target — it showed whatever
// resolved at that moment, which is what selecting the row then changed.
const SYSTEM_DEFAULT_EMAIL = import.meta.env.VITE_APP_DEFAULT_EMAIL;

const systemEmailFor = (data?: {
  default_mailbox_id: string | null;
  resolved_email: string | null;
}): string | null => {
  if (SYSTEM_DEFAULT_EMAIL) return SYSTEM_DEFAULT_EMAIL;
  return data && !data.default_mailbox_id ? data.resolved_email : null;
};

const isActivating = (pn: PhoneNumber) =>
  pn.status === "pending" || pn.status === "pending_approval";

const PhoneNumberControl = ({
  propertyId,
  fallbackNumber,
  activatingNumber,
  selectedValue,
  options,
  noNumbersAvailable,
  onChange
}: {
  propertyId: number;
  fallbackNumber: PhoneNumber | undefined;
  activatingNumber: PhoneNumber | undefined;
  selectedValue: string;
  options: BaseOption[];
  noNumbersAvailable: boolean;
  onChange: (e: { target: { value: string } }) => void;
}) => {
  // Render the dropdown whenever the property has assigned numbers, even if
  // they are all still activating — those show as disabled options instead of
  // being silently hidden. While nothing selectable is assigned, sending
  // still falls back to the account default number, so surface it below.
  if (options.length > 0)
    return (
      <div className="space-y-2">
        <Select
          id={`property-${propertyId}-phone-default`}
          hideOptional
          value={selectedValue}
          onChange={onChange}
          options={options}
          legacyErrorMargins={false}
          className="max-w-[280px]"
        />
        <Show if={!selectedValue && Boolean(fallbackNumber)}>
          <p className="text-sm text-content-400">
            {formatPhone(fallbackNumber?.phone_number ?? "")} — your
            account&apos;s default number — will be used until an assigned
            number becomes active.
          </p>
        </Show>
      </div>
    );

  // No active number assigned to this property, but sending falls back to
  // the partner default number — surface that instead of claiming none exist.
  if (fallbackNumber)
    return (
      <div className="space-y-2">
        <p className="text-sm text-content-1000">
          {formatPhone(fallbackNumber.phone_number ?? "")}
        </p>
        <p className="text-sm text-content-400">
          No phone number is assigned to this property yet, so your
          account&apos;s default number is used. Assign a number to override it.
        </p>
        <div className="pt-1">
          <Link to="/phone-numbers">
            <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
              Manage phone numbers
            </Button>
          </Link>
        </div>
      </div>
    );

  // A number exists but is still completing A2P registration — it can take a
  // few hours after purchase before it becomes active and selectable.
  if (activatingNumber)
    return (
      <div className="space-y-2">
        <p className="text-sm text-content-400">
          {formatPhone(activatingNumber.phone_number ?? "")} is still activating
          and can&apos;t send messages yet. It will appear here automatically
          once activation completes.
        </p>
        <div className="pt-1">
          <Link to="/phone-numbers">
            <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
              Manage phone numbers
            </Button>
          </Link>
        </div>
      </div>
    );

  if (noNumbersAvailable)
    return (
      <div className="space-y-2">
        <p className="text-sm text-content-400">
          No phone numbers are provisioned for your account.
        </p>
        <div className="pt-1">
          <Link to="/phone-numbers">
            <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
              Manage phone numbers
            </Button>
          </Link>
        </div>
      </div>
    );

  return (
    <Link to="/settings/communication">
      <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
        Set up phone numbers
      </Button>
    </Link>
  );
};

const EmailDefaultControl = ({ propertyId }: { propertyId: number }) => {
  const { data, isError } = usePropertyDefaultEmail(propertyId);
  const { mutate: setEmailDefault, isPending } = useSetPropertyDefaultEmail();

  // CRM-1165: the empty row is labelled with the address it produces, and the
  // word "default" is gone — it now only means "assigned to this property".
  const options: BaseOption[] = useMemo(() => {
    const systemEmail = systemEmailFor(data);
    const mailboxes = (data?.available_emails ?? []).map((mailbox) => ({
      value: mailbox.mailbox_id,
      label: mailbox.display_name
        ? `${mailbox.display_name} (${mailbox.email})`
        : mailbox.email
    }));

    // Two rows would otherwise read identically while doing opposite things.
    const collides = (data?.available_emails ?? []).some(
      (mailbox) => mailbox.email === systemEmail
    );
    const collisionSuffix = collides ? " (not assigned)" : "";
    const emptyLabel = systemEmail
      ? `${systemEmail}${collisionSuffix}`
      : "Unassigned";

    // available_emails lists only active SHARED mailboxes, but Settings assigns
    // personal ones too. Without a row the card would claim the system address.
    const assignedId = data?.default_mailbox_id;
    const assignedElsewhere =
      assignedId && !mailboxes.some((option) => option.value === assignedId);
    // Not selectable: the server rejects this id, so offering it would turn
    // re-picking the current sender into a 422.
    const assignedRow: BaseOption[] = assignedElsewhere
      ? [
          {
            value: assignedId,
            label: data?.resolved_email
              ? `${data.resolved_email} (assigned in Settings)`
              : "Assigned in Settings",
            isDisabled: true,
            disabledMessage:
              "Assigned in Settings → Communication. Change it there."
          }
        ]
      : [];

    return [
      { value: CLEAR_DEFAULT_VALUE, label: emptyLabel },
      ...assignedRow,
      ...mailboxes
    ];
  }, [data]);

  // Hides on 403/404 (no property access / feature disabled) or any GET error.
  if (isError || !data) return null;

  const { available_emails, default_mailbox_id } = data;
  // Stays enabled while an assignment exists, so it can still be cleared.
  const hasSelectableMailboxes = available_emails.length > 0;
  const isSelectDisabled =
    isPending || (!hasSelectableMailboxes && !default_mailbox_id);

  const selectedValue =
    default_mailbox_id &&
    options.some((option) => option.value === default_mailbox_id)
      ? default_mailbox_id
      : CLEAR_DEFAULT_VALUE;

  const handleChange = (e: { target: { value: string } }) => {
    const mailboxId =
      e.target.value === CLEAR_DEFAULT_VALUE ? null : e.target.value;
    // react-select fires onChange on re-selecting the current option.
    if (mailboxId === default_mailbox_id) return;
    setEmailDefault(
      { propertyId, mailboxId },
      {
        onSuccess: () => {
          toast.success("Default email updated");
        },
        onError: (error) => {
          const message = axios.isAxiosError<{ message?: string }>(error)
            ? error.response?.data?.message
            : undefined;
          toast.error(message || "Failed to update default email");
        }
      }
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-content-600 mb-1">
        Email
      </label>
      <Select
        id="property-communication-default-email"
        hideOptional
        value={selectedValue}
        onChange={handleChange}
        options={options}
        isDisabled={isSelectDisabled}
        legacyErrorMargins={false}
        className="w-[330px]"
      />
    </div>
  );
};

export const CommunicationDefaultsSection = ({
  propertyId
}: {
  propertyId: number;
}) => {
  const { accessToken } = useInboxToken();

  // Only fetch phone numbers assigned to this property.
  const { data } = useAllPhoneNumbers({
    enabled: !!accessToken,
    propertyIds: [propertyId]
  });
  const { data: currentDefault } = usePropertyPhoneDefault(
    propertyId,
    !!accessToken
  );
  const { mutate: setPropertyDefault } = useSetPropertyPhoneDefault();

  const activeNumbers = useMemo(
    () => data?.items.filter((pn) => pn.status === "active") ?? [],
    [data]
  );

  // When the property has no active number of its own, sending falls back to
  // the partner default number — fetch partner-wide numbers to surface it.
  const { data: partnerData } = useAllPhoneNumbers({
    enabled: !!accessToken && data !== undefined && activeNumbers.length === 0
  });

  const partnerActiveNumbers = useMemo(
    () => partnerData?.items.filter((pn) => pn.status === "active") ?? [],
    [partnerData]
  );

  const fallbackNumber =
    partnerActiveNumbers.find((pn) => pn.is_default) ?? partnerActiveNumbers[0];

  // Prefer a number assigned to this property that is still activating;
  // otherwise fall back to any partner number still activating.
  const activatingNumber =
    data?.items.find(isActivating) ?? partnerData?.items.find(isActivating);

  const noNumbersAvailable =
    data !== undefined &&
    partnerData !== undefined &&
    activeNumbers.length === 0 &&
    !fallbackNumber &&
    !activatingNumber;

  // Active numbers are selectable; activating ones are listed but disabled so
  // users can see a number is on its way instead of it silently missing.
  const options: BaseOption[] = useMemo(() => {
    const active: BaseOption[] = [];
    const activating: BaseOption[] = [];
    (data?.items ?? []).forEach((pn) => {
      if (pn.status === "active")
        active.push({
          value: pn.communication_service_id,
          label: formatPhone(pn.phone_number ?? "")
        });
      else if (isActivating(pn))
        activating.push({
          value: pn.communication_service_id,
          label: `${formatPhone(pn.phone_number ?? "")} (activating…)`,
          isDisabled: true,
          disabledMessage:
            "This number is still completing activation and can't send messages yet."
        });
    });
    return [...active, ...activating];
  }, [data]);

  const partnerDefaultId = activeNumbers.find(
    (pn) => pn.is_default
  )?.communication_service_id;

  const selectedValue =
    currentDefault?.phone_number_id ||
    partnerDefaultId ||
    activeNumbers[0]?.communication_service_id ||
    "";

  const handleChange = (e: { target: { value: string } }) => {
    setPropertyDefault(
      { propertyId, phoneNumberId: e.target.value },
      {
        onSuccess: () => {
          toast.success("Property phone number default updated");
        },
        onError: (error) => {
          const message = axios.isAxiosError<{ message?: string }>(error)
            ? error.response?.data?.message
            : undefined;
          toast.error(message || "Failed to update default");
        }
      }
    );
  };

  return (
    <SectionCard
      title="Communication defaults"
      subtitle="Applicants will receive communications from this entity"
    >
      <div className="flex gap-6">
        <div>
          <label className="block text-sm font-medium text-content-600 mb-1">
            Phone number
          </label>
          <PhoneNumberControl
            propertyId={propertyId}
            fallbackNumber={fallbackNumber}
            activatingNumber={activatingNumber}
            selectedValue={selectedValue}
            options={options}
            noNumbersAvailable={noNumbersAvailable}
            onChange={handleChange}
          />
        </div>
        <EmailDefaultControl propertyId={propertyId} />
      </div>
    </SectionCard>
  );
};
